<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import { vClickOutside } from '~/directives/ClickOutside'

import { useLanguage } from '~/composables/useLanguage'

import { useUserStore } from '~/stores/user'

import type { Language } from '~/types/language'

/**
 * LanguageSwitcher is soon to be deprecated in favor of LanguageSwitcherV2.
 */

type Props = {
  dropdownPosition?: 'bottom' | 'top'
  localeVerbosity?: 'full-length' | 'short'
}
type Slots = {
  'left-text'(): any
}

withDefaults(defineProps<Props>(), {
  dropdownPosition: 'top',
  localeVerbosity: 'full-length',
})
defineSlots<Slots>()

const isMenuVisible = ref(false)

const { email: USER_EMAIL } = storeToRefs(useUserStore())

const {
  getCurrentLanguage,
  setCurrentLanguage,
  sortedAvailableLanguageChoices: allAvailableLanguages,
} = useLanguage()

const currentLanguage = computed(() => getCurrentLanguage())

async function switchToLanguage(language: Language) {
  closeMenu()
  return navigateTo(await setCurrentLanguage(language, USER_EMAIL.value))
}
const sortedAvailableLanguageChoices = computed(() => {
  return allAvailableLanguages.value.filter(
    (language) => language !== currentLanguage.value,
  )
})

function closeMenu() {
  isMenuVisible.value = false
}
</script>

<template>
  <div v-click-outside="closeMenu" class="tw-relative tw-w-full">
    <Transition name="fade" mode="out-in">
      <div
        v-if="isMenuVisible"
        class="tw-pointer tw-absolute tw-right-0 tw-grid tw-w-full tw-grid-cols-2 tw-flex-col tw-gap-x-4 tw-rounded-t tw-bg-white tw-p-2 md:tw-w-56"
        :class="{
          'tw-bottom-10': dropdownPosition === 'top',
          'tw-top-10': dropdownPosition === 'bottom',
        }"
      >
        <button
          v-for="language in sortedAvailableLanguageChoices"
          :key="language"
          type="button"
          class="tw-ellipsis tw-text-body-sm tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-px-5 tw-text-default-color tw-transition-colors hover:tw-bg-gray-200"
          @click="switchToLanguage(language)"
        >
          {{
            $t(
              `${localeVerbosity === 'full-length' ? 'locales' : 'localesShort'}.${language}`,
            )
          }}
        </button>
      </div>
    </Transition>

    <div
      class="tw-ellipsis tw-flex tw-w-full tw-justify-center tw-gap-1 sm:tw-w-auto sm:tw-justify-end"
    >
      <span
        ><slot name="left-text">{{
          $t('components.ui.langSwitcher.lang')
        }}</slot></span
      >
      <div class="tw-flex" @click="isMenuVisible = !isMenuVisible">
        <strong class="tw-mr-1 tw-cursor-pointer tw-select-none">{{
          $t(
            `${localeVerbosity === 'full-length' ? 'locales' : 'localesShort'}.${currentLanguage}`,
          )
        }}</strong>
        <UIIcon
          name="fa6-solid:chevron-down"
          size="sm"
          class="tw-origin-center tw-self-center tw-text-icon-abovePrimary tw-transition-transform"
          :class="{ 'tw-rotate-180': isMenuVisible }"
        />
      </div>
    </div>
  </div>
</template>
